// schema.org JSON-LD builders shared by every page and the prerender step.

export type JsonLdNode = Record<string, unknown>;

export const SITE_ORIGIN = "https://foxmark.co";

const OG_IMAGE = `${SITE_ORIGIN}/og-image-v2.png`;
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const SITE_ID = `${SITE_ORIGIN}/#website`;

export function canonicalUrl(path: string): string {
  return path === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${path}`;
}

export const organizationNode: JsonLdNode = {
  "@type": "Organization",
  "@id": ORG_ID,
  name: "Foxmark",
  url: `${SITE_ORIGIN}/`,
  logo: `${SITE_ORIGIN}/android-chrome-512x512.png`,
  image: OG_IMAGE,
  description:
    "Foxmark helps local businesses find the gaps that cost calls, bookings, and trust with monthly review, profile, and website reports."
};

export const websiteNode: JsonLdNode = {
  "@type": "WebSite",
  "@id": SITE_ID,
  url: `${SITE_ORIGIN}/`,
  name: "Foxmark",
  inLanguage: "en",
  publisher: { "@id": ORG_ID }
};

export function serviceNode(input: {
  name: string;
  serviceType: string;
  path: string;
  description: string;
  areaServed?: string;
}): JsonLdNode {
  const url = canonicalUrl(input.path);
  return {
    "@type": "Service",
    "@id": `${SITE_ORIGIN}${input.path}#service`,
    name: input.name,
    serviceType: input.serviceType,
    url,
    areaServed: input.areaServed ?? "United States",
    provider: { "@id": ORG_ID },
    description: input.description,
    offers: {
      "@type": "Offer",
      price: "149",
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url,
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        price: "149",
        priceCurrency: "USD",
        referenceQuantity: { "@type": "QuantitativeValue", value: "1", unitCode: "MON" }
      }
    }
  };
}

export type Faq = { question: string; answer: string };

export function faqPageNode(faqs: readonly Faq[]): JsonLdNode {
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer }
    }))
  };
}

export function breadcrumbNode(items: readonly { name: string; path: string }[]): JsonLdNode {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonicalUrl(item.path)
    }))
  };
}

export function articleNode(input: {
  headline: string;
  description: string;
  path: string;
}): JsonLdNode {
  const url = canonicalUrl(input.path);
  return {
    "@type": "Article",
    headline: input.headline,
    description: input.description,
    url,
    mainEntityOfPage: url,
    author: { "@id": ORG_ID },
    publisher: { "@id": ORG_ID }
  };
}

export function graph(nodes: readonly JsonLdNode[]): JsonLdNode {
  return { "@context": "https://schema.org", "@graph": nodes };
}
